import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  companyIdentityApi,
  type Brand,
  type BrandLogo,
  type BrandLogoSet,
  type BrandPalette,
} from '../lib/api.js';

/// Tudo o que a web guarda da identidade da empresa começa com esta chave: invalidar o prefixo
/// depois de trocar nome, cor ou logo refaz a casca, a tela de dados e as prévias de uma vez.
export const brandQueryKey = ['tenant', 'brand'] as const;

/// A cor da empresa como sai no papel: o tom principal do tema claro de cada paleta
/// (`--brand-500` em `packages/design-system/src/styles.css`), o mesmo que a API usa no PDF.
export const brandPrintColors: Readonly<Record<BrandPalette, string>> = {
  hue: '#8a6012',
  mono: '#26262a',
  navy: '#1f3d5c',
  moss: '#304b2d',
  wine: '#65282f',
  straw: '#54411d',
  blossom: '#573041',
};

export const brandPaletteLabels: Readonly<Record<BrandPalette, string>> = {
  hue: 'HUE',
  mono: 'Mono',
  navy: 'Navy',
  moss: 'Moss',
  wine: 'Wine',
  straw: 'Straw',
  blossom: 'Blossom',
};

export const EMPTY_LOGOS: BrandLogoSet = { MAIN: null, SYMBOL: null, DARK: null };

/// A identidade resolvida para a unidade em que a pessoa está. A chave carrega a unidade porque
/// trocar de unidade troca as logos próprias.
export function useBrand(scope: string | null | undefined) {
  return useQuery<Brand>({
    queryKey: [...brandQueryKey, 'shell', scope ?? 'company'],
    queryFn: companyIdentityApi.brand,
    staleTime: 5 * 60_000,
  });
}

/// O endereço local do PNG de uma logo. O identificador muda a cada troca, então o arquivo baixado
/// vale para sempre.
export function useLogoUrl(logo: BrandLogo | null | undefined, size: 'small' | 'large' = 'small') {
  const query = useQuery({
    queryKey: [...brandQueryKey, 'file', logo?.id ?? null, size],
    queryFn: async () => URL.createObjectURL(await companyIdentityApi.logoFile(logo!.id, size)),
    enabled: Boolean(logo),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });
  return logo ? query.data : undefined;
}

/// Claro ou escuro, acompanhando a escolha da pessoa, que o seletor de aparência grava no `<html>`.
export function useColorMode(): 'light' | 'dark' {
  const read = () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark');
  const [mode, setMode] = useState<'light' | 'dark'>(read);
  useEffect(() => {
    const observer = new MutationObserver(() => setMode(read()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);
  return mode;
}

export function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/u)
      .filter((word) => /\p{L}|\d/u.test(word))
      .slice(0, 2)
      .map((word) => word.charAt(0).toLocaleUpperCase('pt-BR'))
      .join('') || '?'
  );
}
